// src/components/CaseDetails.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast, { Toaster } from "react-hot-toast";
import {
  FaArrowLeft,
  FaTrash,
  FaFileUpload,
  FaUserTie,
  FaRegFilePdf,
  FaTimes,
  FaCamera,
  FaMoneyBillWave,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileWord,
  FaFileImage,
  FaCalendarAlt,
  FaClock,
  FaPlus,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

function CaseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  // files
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  // payments
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  // camera modal
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // meetings (add)
  const [meetingForm, setMeetingForm] = useState({
    title: "",
    date: "",
    time: "",
    desc: "",
    remindMinutes: 0,
  });
  const [addingMeeting, setAddingMeeting] = useState(false);

  // complete meeting modal
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    resultType: "تأجيل",
    resultNote: "",
    nextDate: "",
    nextTime: "",
  });
  const [completing, setCompleting] = useState(false);

  /* =======================
     HELPERS
  ======================= */
  const safeText = (v) => (typeof v === "string" ? v.trim() : "");

  const normalizeTime = (t) => {
    if (!t) return "";
    const s = String(t).trim();
    if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(s)) return s;
    return "";
  };

  const getFileIcon = (fileName = "", fileType = "") => {
    const name = fileName.toLowerCase();

    if (fileType.startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(name))
      return <FaFileImage className="text-yellow-300 shrink-0" />;

    if (fileType === "application/pdf" || name.endsWith(".pdf"))
      return <FaRegFilePdf className="text-red-400 shrink-0" />;

    if (name.endsWith(".doc") || name.endsWith(".docx"))
      return <FaFileWord className="text-blue-400 shrink-0" />;

    return <FaFileAlt className="text-gray-300 shrink-0" />;
  };

  const isPreviewable = (fileName = "", fileType = "") => {
    const name = fileName.toLowerCase();
    return (
      fileType.startsWith("image/") ||
      fileType === "application/pdf" ||
      name.endsWith(".pdf") ||
      /\.(png|jpg|jpeg|webp|gif)$/i.test(name)
    );
  };

  const formatDateAr = (date) => {
    try {
      return new Date(date).toLocaleDateString("ar-EG", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      });
    } catch {
      return "—";
    }
  };

  const formatDateShort = (date) => {
    try {
      return new Date(date).toLocaleDateString("ar-EG");
    } catch {
      return "—";
    }
  };

  const getMeetingDateTimeKey = (m) => {
    const d = new Date(m.date);
    const [hh, mm] = m.time ? String(m.time).split(":").map(Number) : [9, 0];
    d.setHours(hh || 0, mm || 0, 0, 0);
    return d.getTime();
  };

  const getMeetingStatusLabel = (status) => {
    if (status === "completed") return "✅ مكتملة";
    if (status === "cancelled") return "❌ ملغاة";
    return "🟡 قادمة";
  };

  const getMeetingStatusStyle = (status) => {
    if (status === "completed")
      return "border-green-600 bg-green-600/10 text-green-200";
    if (status === "cancelled")
      return "border-red-600 bg-red-600/10 text-red-200";
    return "border-yellow-600 bg-yellow-600/10 text-yellow-200";
  };

  /* =======================
     FETCH CASE
  ======================= */
  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/cases/${id}`);
      setCaseData(res.data.case);
    } catch (err) {
      toast.error("❌ فشل تحميل بيانات القضية");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* =======================
     PAYMENTS CALCULATIONS
  ======================= */
  const totalPaid = useMemo(() => {
    return caseData?.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
  }, [caseData]);

  const remaining = useMemo(() => {
    return (caseData?.price || 0) - totalPaid;
  }, [caseData, totalPaid]);

  /* =======================
     ADD PAYMENT
  ======================= */
  const addPayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0)
      return toast.error("💵 أدخل مبلغ صحيح");

    try {
      await api.post(`/cases/${id}/payments`, {
        amount: paymentAmount,
        note: paymentNote,
      });

      toast.success("✅ تم تسجيل الدفعة");
      setPaymentAmount("");
      setPaymentNote("");
      fetchCaseDetails();
    } catch {
      toast.error("❌ فشل إضافة الدفعة");
    }
  };

  /* =======================
     CASE MEETINGS (Timeline)
  ======================= */
  const sortedMeetings = useMemo(() => {
    const arr = Array.isArray(caseData?.meetings) ? [...caseData.meetings] : [];

    // pending first (closest first), then completed (latest first)
    const pending = arr
      .filter((m) => (m.status || "pending") !== "completed")
      .sort((a, b) => getMeetingDateTimeKey(a) - getMeetingDateTimeKey(b));

    const completed = arr
      .filter((m) => (m.status || "pending") === "completed")
      .sort((a, b) => getMeetingDateTimeKey(b) - getMeetingDateTimeKey(a));

    return [...pending, ...completed];
  }, [caseData]);

  const addMeetingToCase = async () => {
    const title = safeText(meetingForm.title);
    const date = safeText(meetingForm.date);

    if (!title || !date) return toast.error("⚠️ لازم عنوان + تاريخ للجلسة");

    try {
      setAddingMeeting(true);

      // ✅ IMPORTANT: correct endpoint (linked to the case)
      const res = await api.post(`/cases/${id}/meetings`, {
        title,
        date,
        time: normalizeTime(meetingForm.time),
        desc: safeText(meetingForm.desc),
        remindMinutes: Number(meetingForm.remindMinutes || 0),
      });

      toast.success("✅ تم إضافة جلسة للقضية");

      // backend returns updated case
      if (res.data?.case) setCaseData(res.data.case);
      else await fetchCaseDetails();

      setMeetingForm({ title: "", date: "", time: "", desc: "", remindMinutes: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل إضافة الجلسة");
    } finally {
      setAddingMeeting(false);
    }
  };

  const openCompleteModal = (m) => {
    setCompleteTarget(m);
    setCompleteForm({
      resultType: "تأجيل",
      resultNote: "",
      nextDate: "",
      nextTime: "",
    });
    setCompleteOpen(true);
  };

  const completeMeeting = async () => {
    if (!completeTarget?._id) return;

    try {
      setCompleting(true);

      const res = await api.put(
        `/cases/${id}/meetings/${completeTarget._id}/complete`,
        {
          resultType: safeText(completeForm.resultType),
          resultNote: safeText(completeForm.resultNote),
          nextDate: safeText(completeForm.nextDate),
          nextTime: normalizeTime(completeForm.nextTime),
        }
      );

      toast.success("✅ تم تسجيل نتيجة الجلسة");
      setCompleteOpen(false);
      setCompleteTarget(null);

      if (res.data?.case) setCaseData(res.data.case);
      else await fetchCaseDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل تسجيل النتيجة");
    } finally {
      setCompleting(false);
    }
  };

  const deleteMeetingFromCase = async (meetingId) => {
    if (!window.confirm("هل تريد حذف هذه الجلسة؟")) return;

    try {
      const res = await api.delete(`/cases/${id}/meetings/${meetingId}`);
      toast.success("🗑️ تم حذف الجلسة");
      if (res.data?.case) setCaseData(res.data.case);
      else fetchCaseDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل حذف الجلسة");
    }
  };

  /* =======================
     UPLOAD FILE
  ======================= */
  const handleUpload = async () => {
    if (!file) return toast.error("📎 اختر ملف أولاً");

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);

      const res = await api.post(`/cases/${id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("✅ تم رفع الملف");
      if (res.data?.case) setCaseData(res.data.case);

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل رفع الملف");
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm("⚠️ هل تريد حذف هذا الملف؟")) return;

    try {
      const res = await api.delete(`/cases/${id}/files/${fileId}`);
      toast.success("🗑️ تم حذف الملف");
      if (res.data?.case) setCaseData(res.data.case);
      else fetchCaseDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل حذف الملف");
    }
  };

  /* =======================
     CAMERA
  ======================= */
  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch {}
  };

  const startCamera = async () => {
    try {
      setCameraLoading(true);
      setCapturedBlob(null);

      if (capturedPreview) URL.revokeObjectURL(capturedPreview);
      setCapturedPreview(null);

      setCameraOpen(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        await videoRef.current.play();
      }
    } catch (err) {
      toast.error("❌ فشل فتح الكاميرا (اسمح بالموافقة)");
      setCameraOpen(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const takePhoto = async () => {
    try {
      const video = videoRef.current;
      if (!video) return;

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92)
      );

      if (!blob) return toast.error("❌ فشل التقاط الصورة");

      setCapturedBlob(blob);
      setCapturedPreview(URL.createObjectURL(blob));
      stopCamera();
    } catch {
      toast.error("❌ حصل خطأ أثناء التصوير");
    }
  };

  const uploadCapturedPhoto = async () => {
    if (!capturedBlob) return toast.error("❌ لا توجد صورة لرفعها");

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", capturedBlob, `capture_${Date.now()}.jpg`);

      const res = await api.post(`/cases/${id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("✅ تم رفع الصورة");
      if (res.data?.case) setCaseData(res.data.case);

      setCameraOpen(false);
      setCapturedBlob(null);
      if (capturedPreview) URL.revokeObjectURL(capturedPreview);
      setCapturedPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل رفع الصورة");
    } finally {
      setUploading(false);
    }
  };

  const retakePhoto = async () => {
    setCapturedBlob(null);
    if (capturedPreview) URL.revokeObjectURL(capturedPreview);
    setCapturedPreview(null);
    await startCamera();
  };

  useEffect(() => {
    return () => {
      stopCamera();
      if (capturedPreview) URL.revokeObjectURL(capturedPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================
     DELETE CASE
  ======================= */
  const handleDeleteCase = async () => {
    if (!window.confirm("⚠️ هل تريد حذف هذه القضية؟")) return;
    try {
      await api.delete(`/cases/${id}`);
      toast.success("🗑️ تم حذف القضية");
      navigate("/cases");
    } catch {
      toast.error("❌ فشل حذف القضية");
    }
  };

  /* =======================
     UI STATES
  ======================= */
  if (loading) {
    return (
      <div className="text-center text-yellow-400 mt-20 text-xl animate-pulse px-4">
        ⏳ جاري تحميل البيانات...
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center text-red-500 mt-20 text-xl px-4">
        ❌ لم يتم العثور على القضية
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-b from-gray-950 via-black to-gray-900 min-h-screen text-white">
      <Toaster position="top-center" />

      {/* TOP ACTIONS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <button
          onClick={() => navigate("/cases")}
          className="w-full sm:w-auto bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-xl flex items-center justify-center gap-2 font-bold"
        >
          <FaArrowLeft /> العودة
        </button>

        <button
          onClick={handleDeleteCase}
          className="w-full sm:w-auto bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl flex items-center justify-center gap-2 font-bold"
        >
          <FaTrash /> حذف
        </button>
      </div>

      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-yellow-400">
          ⚖️ تفاصيل القضية
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          كل جلسة لها نتيجتها وتأجيلاتها — Timeline كامل للقضية.
        </p>
      </div>

      {/* CASE INFO */}
      <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl border border-yellow-500 mb-6 shadow-xl">
        <h2 className="text-xl sm:text-2xl font-extrabold text-yellow-300 mb-2 break-words">
          {caseData.title}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm sm:text-base">
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">⚖️ النوع</p>
            <p className="font-bold">{caseData.type || "—"}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">📅 تاريخ البداية</p>
            <p className="font-bold">{formatDateShort(caseData.startDate)}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">📌 تاريخ الجلسة الحالية</p>
            <p className="font-bold">
              {caseData.trialDate ? formatDateShort(caseData.trialDate) : "—"}
            </p>
          </div>

          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">📝 ملاحظات</p>
            <p className="font-bold break-words">{caseData.notes || "—"}</p>
          </div>
        </div>
      </div>

      {/* CLIENT */}
      <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl border border-yellow-500 mb-6 shadow-xl">
        <h3 className="text-lg sm:text-xl font-extrabold text-yellow-300 mb-3 flex items-center gap-2">
          <FaUserTie /> بيانات العميل
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm sm:text-base">
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">👤 الاسم</p>
            <p className="font-bold break-words">{caseData.client?.name || "—"}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">📞 الهاتف</p>
            <p className="font-bold break-words">{caseData.client?.phone || "—"}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">📌 رقم التوكيل</p>
            <p className="font-bold break-words">{caseData.client?.email || "—"}</p>
          </div>
        </div>
      </div>

      {/* MEETINGS TIMELINE */}
      <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl border border-yellow-500 mb-6 shadow-xl">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-lg sm:text-xl font-extrabold text-yellow-300 flex items-center gap-2">
            <FaCalendarAlt /> جلسات القضية (Timeline)
          </h3>
        </div>

        {/* Add meeting form */}
        <div className="bg-gray-800 p-4 rounded-2xl border border-gray-700 mb-5">
          <h4 className="font-extrabold text-yellow-200 mb-3 flex items-center gap-2">
            <FaPlus /> إضافة جلسة مرتبطة بالقضية
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              className="bg-gray-900 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
              placeholder="عنوان الجلسة (مثال: جلسة محكمة)"
              value={meetingForm.title}
              onChange={(e) =>
                setMeetingForm((p) => ({ ...p, title: e.target.value }))
              }
            />

            <input
              type="date"
              className="bg-gray-900 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
              value={meetingForm.date}
              onChange={(e) =>
                setMeetingForm((p) => ({ ...p, date: e.target.value }))
              }
            />

            <input
              type="time"
              className="bg-gray-900 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
              value={meetingForm.time}
              onChange={(e) =>
                setMeetingForm((p) => ({ ...p, time: e.target.value }))
              }
            />

            <select
              className="bg-gray-900 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
              value={meetingForm.remindMinutes}
              onChange={(e) =>
                setMeetingForm((p) => ({
                  ...p,
                  remindMinutes: Number(e.target.value),
                }))
              }
            >
              <option value={0}>تنبيه في نفس الوقت</option>
              <option value={5}>قبلها بـ 5 دقائق</option>
              <option value={10}>قبلها بـ 10 دقائق</option>
              <option value={15}>قبلها بـ 15 دقيقة</option>
              <option value={30}>قبلها بـ 30 دقيقة</option>
            </select>

            <textarea
              className="bg-gray-900 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500 md:col-span-2"
              rows={3}
              placeholder="وصف / ملاحظات (اختياري)"
              value={meetingForm.desc}
              onChange={(e) =>
                setMeetingForm((p) => ({ ...p, desc: e.target.value }))
              }
            />

            <button
              onClick={addMeetingToCase}
              disabled={addingMeeting}
              className="md:col-span-2 bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold px-5 py-3 rounded-xl disabled:opacity-70"
            >
              {addingMeeting ? "⏳ جاري الإضافة..." : "✅ إضافة الجلسة"}
            </button>
          </div>
        </div>

        {/* Meetings list */}
        {sortedMeetings.length > 0 ? (
          <div className="space-y-3">
            {sortedMeetings.map((m) => {
              const status = m.status || "pending";
              const done = status === "completed";

              return (
                <div
                  key={m._id}
                  className={`p-4 rounded-2xl border ${getMeetingStatusStyle(status)} shadow-md`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-lg font-extrabold text-yellow-100 break-words">
                          {m.title}
                        </p>

                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-black/30 border border-white/10">
                          {getMeetingStatusLabel(status)}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-gray-100 space-y-1">
                        <p className="flex items-center gap-2">
                          <FaCalendarAlt className="text-yellow-300" />
                          <span>{formatDateAr(m.date)}</span>
                        </p>

                        <p className="flex items-center gap-2">
                          <FaClock className="text-yellow-300" />
                          <span>{m.time || "بدون وقت"}</span>
                        </p>

                        {m.desc ? (
                          <p className="text-gray-200 break-words mt-2">
                            📝 {m.desc}
                          </p>
                        ) : null}

                        {/* ✅ Show results */}
                        {done ? (
                          <div className="mt-3 p-3 rounded-xl bg-black/25 border border-white/10">
                            <p className="font-extrabold text-green-200 flex items-center gap-2">
                              <FaCheckCircle /> آخر تحديث: تم تسجيل نتيجة الجلسة
                            </p>

                            {(m.resultType || m.resultNote) && (
                              <p className="text-gray-50 mt-2 break-words">
                                ✅ النتيجة:{" "}
                                <span className="font-extrabold">
                                  {m.resultType || "—"}
                                </span>
                                {m.resultNote ? ` — ${m.resultNote}` : ""}
                              </p>
                            )}

                            {m.nextDate ? (
                              <p className="text-gray-50 mt-2">
                                📌 الجلسة القادمة:{" "}
                                <span className="font-extrabold">
                                  {formatDateShort(m.nextDate)}
                                </span>
                                {m.nextTime ? ` — ${m.nextTime}` : ""}
                              </p>
                            ) : (
                              <p className="text-gray-200 mt-2 flex items-center gap-2">
                                <FaInfoCircle className="text-gray-300" />
                                لا يوجد ميعاد جديد مسجل بعد.
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3 p-3 rounded-xl bg-black/25 border border-white/10">
                            <p className="text-yellow-100 font-extrabold">
                              🟡 جلسة قادمة (لم يتم إنهاؤها بعد)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {!done ? (
                        <button
                          onClick={() => openCompleteModal(m)}
                          className="bg-green-500 hover:bg-green-600 text-black font-extrabold px-4 py-2 rounded-xl"
                        >
                          ✅ إنهاء الجلسة
                        </button>
                      ) : null}

                      <button
                        onClick={() => deleteMeetingFromCase(m._id)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 rounded-xl"
                      >
                        🗑️ حذف
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400">لا توجد جلسات لهذه القضية بعد.</p>
        )}
      </div>

      {/* PAYMENTS */}
      <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl border border-green-600 mb-6 shadow-xl">
        <h3 className="text-lg sm:text-xl font-extrabold text-green-400 mb-4 flex items-center gap-2">
          <FaMoneyBillWave /> الدفعات المالية
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 text-center">
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400">الإجمالي</p>
            <p className="text-yellow-400 font-extrabold">{caseData.price} ج.م</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400">المدفوع</p>
            <p className="text-green-400 font-extrabold">{totalPaid} ج.م</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400">المتبقي</p>
            <p
              className={`font-extrabold ${
                remaining > 0 ? "text-red-400" : "text-green-400"
              }`}
            >
              {remaining} ج.م
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input
            type="number"
            placeholder="مبلغ الدفعة"
            className="bg-gray-800 p-3 rounded-xl text-white w-full md:w-1/3 border border-gray-700 outline-none focus:border-green-500"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />

          <input
            placeholder="ملاحظة"
            className="bg-gray-800 p-3 rounded-xl text-white w-full md:flex-1 border border-gray-700 outline-none focus:border-green-500"
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
          />

          <button
            onClick={addPayment}
            className="bg-green-500 hover:bg-green-600 text-black px-6 py-3 rounded-xl font-extrabold w-full md:w-auto"
          >
            إضافة
          </button>
        </div>

        {caseData.payments?.length > 0 ? (
          <ul className="space-y-2">
            {caseData.payments.map((p, i) => (
              <li
                key={i}
                className="bg-gray-800 p-3 rounded-xl text-sm sm:text-base break-words border border-gray-700"
              >
                💰 {p.amount} ج.م – {new Date(p.date).toLocaleString("ar-EG")}
                {p.note ? <span> | 📝 {p.note}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400">لا توجد دفعات</p>
        )}
      </div>

      {/* FILES */}
      <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl border border-yellow-500 shadow-xl">
        <h3 className="text-lg sm:text-xl font-extrabold text-yellow-300 mb-4 flex items-center gap-2">
          <FaRegFilePdf /> الملفات
        </h3>

        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="*/*"
            className="w-full md:w-auto"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="bg-yellow-500 hover:bg-yellow-600 px-4 py-3 rounded-xl text-black flex items-center justify-center gap-2 w-full md:w-auto disabled:opacity-70 font-extrabold"
          >
            <FaFileUpload />
            {uploading ? "جاري الرفع..." : "رفع"}
          </button>

          <button
            onClick={startCamera}
            className="bg-green-500 hover:bg-green-600 px-4 py-3 rounded-xl text-black flex items-center justify-center gap-2 w-full md:w-auto font-extrabold"
          >
            <FaCamera />
            كاميرا
          </button>
        </div>

        <p className="text-xs sm:text-sm text-gray-400 mt-3">
          💡 يمكنك رفع أي نوع ملفات (صور / PDF / Word / أي ملفات أخرى)
        </p>

        {/* FILES LIST */}
        <div className="mt-6">
          {caseData.files?.length > 0 ? (
            <div className="space-y-3">
              {caseData.files.map((f) => (
                <div
                  key={f._id}
                  className="bg-gray-800 p-3 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-700"
                >
                  <div className="min-w-0">
                    <p className="font-extrabold break-words flex items-center gap-2">
                      {getFileIcon(f.fileName, f.fileType || "")}
                      <span className="truncate">{f.fileName}</span>
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      {f.uploadedAt
                        ? new Date(f.uploadedAt).toLocaleString("ar-EG")
                        : ""}
                      {f.fileType ? ` | ${f.fileType}` : ""}
                    </p>

                    {f.ocrText ? (
                      <p className="text-xs text-gray-300 mt-2 break-words">
                        🧠 OCR: {f.ocrText.slice(0, 180)}
                        {f.ocrText.length > 180 ? "..." : ""}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {isPreviewable(f.fileName, f.fileType || "") && (
                      <button
                        onClick={() => setPreviewFile(f.filePath)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-xl font-extrabold"
                      >
                        معاينة
                      </button>
                    )}

                    <a
                      href={f.filePath}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-xl font-extrabold flex items-center gap-2"
                    >
                      فتح <FaExternalLinkAlt className="text-xs" />
                    </a>

                    <button
                      onClick={() => deleteFile(f._id)}
                      className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl font-extrabold"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 mt-3">لا توجد ملفات مرفوعة</p>
          )}
        </div>
      </div>

      {/* PREVIEW MODAL */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-3 sm:p-6 z-50">
          <div className="relative w-full sm:w-4/5 lg:w-3/4 h-[80vh] bg-gray-900 rounded-2xl overflow-hidden border border-gray-700">
            <button
              onClick={() => setPreviewFile(null)}
              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 p-2 rounded-xl z-10"
            >
              <FaTimes />
            </button>

            <iframe src={previewFile} title="preview" className="w-full h-full" />
          </div>
        </div>
      )}

      {/* CAMERA MODAL */}
      {cameraOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-3 sm:p-6 z-50">
          <div className="relative w-full sm:w-4/5 lg:w-1/2 bg-gray-900 rounded-2xl overflow-hidden border border-gray-700">
            <button
              onClick={() => {
                stopCamera();
                setCameraOpen(false);
              }}
              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 p-2 rounded-xl z-10"
            >
              <FaTimes />
            </button>

            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-extrabold text-yellow-400">📷 الكاميرا</h3>
              <p className="text-xs text-gray-400 mt-1">
                التقط صورة ثم ارفعها داخل القضية
              </p>
            </div>

            <div className="p-4">
              {!capturedPreview ? (
                <div className="w-full aspect-[3/4] bg-black rounded-xl overflow-hidden flex items-center justify-center">
                  {cameraLoading ? (
                    <p className="text-gray-300 animate-pulse">
                      ⏳ جاري فتح الكاميرا...
                    </p>
                  ) : (
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                    />
                  )}
                </div>
              ) : (
                <div className="w-full aspect-[3/4] bg-black rounded-xl overflow-hidden">
                  <img
                    src={capturedPreview}
                    alt="captured"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                {!capturedPreview ? (
                  <button
                    onClick={takePhoto}
                    disabled={cameraLoading}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-xl font-extrabold w-full"
                  >
                    📸 تصوير
                  </button>
                ) : (
                  <>
                    <button
                      onClick={uploadCapturedPhoto}
                      disabled={uploading}
                      className="bg-green-500 hover:bg-green-600 text-black px-4 py-3 rounded-xl font-extrabold w-full"
                    >
                      {uploading ? "⏳ جاري الرفع..." : "✅ رفع الصورة"}
                    </button>

                    <button
                      onClick={retakePhoto}
                      className="bg-gray-700 hover:bg-gray-600 px-4 py-3 rounded-xl font-extrabold w-full"
                    >
                      🔄 إعادة التصوير
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COMPLETE MEETING MODAL */}
      {completeOpen && completeTarget && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-3 sm:p-6 z-50">
          <div className="relative w-full max-w-2xl bg-gray-900 rounded-2xl border border-gray-700 overflow-hidden">
            <button
              onClick={() => setCompleteOpen(false)}
              className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 p-2 rounded-xl z-10"
            >
              <FaTimes />
            </button>

            <div className="p-5 border-b border-gray-700">
              <h3 className="text-xl font-extrabold text-yellow-300">
                ✅ إنهاء الجلسة وتسجيل النتيجة
              </h3>
              <p className="text-gray-400 text-sm mt-1 break-words">
                {completeTarget.title} — {formatDateShort(completeTarget.date)}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-gray-300 text-sm">نوع النتيجة</label>
                <select
                  className="mt-1 w-full bg-gray-800 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
                  value={completeForm.resultType}
                  onChange={(e) =>
                    setCompleteForm((p) => ({
                      ...p,
                      resultType: e.target.value,
                    }))
                  }
                >
                  <option value="تأجيل">تأجيل</option>
                  <option value="حكم">حكم</option>
                  <option value="استئناف">استئناف</option>
                  <option value="طلب">طلب / إجراء</option>
                  <option value="أخرى">أخرى</option>
                </select>
              </div>

              <div>
                <label className="text-gray-300 text-sm">ملاحظات / ماذا حدث؟</label>
                <textarea
                  rows={4}
                  className="mt-1 w-full bg-gray-800 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
                  value={completeForm.resultNote}
                  onChange={(e) =>
                    setCompleteForm((p) => ({
                      ...p,
                      resultNote: e.target.value,
                    }))
                  }
                  placeholder="اكتب ملخص الجلسة: تم التأجيل لسبب كذا / صدر حكم / ..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-300 text-sm">
                    تاريخ الجلسة القادمة (اختياري)
                  </label>
                  <input
                    type="date"
                    className="mt-1 w-full bg-gray-800 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
                    value={completeForm.nextDate}
                    onChange={(e) =>
                      setCompleteForm((p) => ({
                        ...p,
                        nextDate: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="text-gray-300 text-sm">
                    وقت الجلسة القادمة (اختياري)
                  </label>
                  <input
                    type="time"
                    className="mt-1 w-full bg-gray-800 p-3 rounded-xl border border-gray-700 outline-none focus:border-yellow-500"
                    value={completeForm.nextTime}
                    onChange={(e) =>
                      setCompleteForm((p) => ({
                        ...p,
                        nextTime: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <button
                onClick={completeMeeting}
                disabled={completing}
                className="w-full bg-green-500 hover:bg-green-600 text-black font-extrabold px-5 py-3 rounded-xl disabled:opacity-70"
              >
                {completing ? "⏳ جاري الحفظ..." : "✅ حفظ نتيجة الجلسة"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseDetails;
